import React, { FC, useRef } from 'react';
import { Col, Row, Divider, Input, Form, Tooltip, FormInstance } from 'antd';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import ProductIsBlockedModal from '../modals/ProductIsBlockedModal';
import ModalStore from '../../../../stores/ModalStore';
import useStore from '../../../../hooks/useStore';
import PanDropdown from './PanDropdown';
import {
  IdentifierInfo,
  IdentifierStatus,
  ProductInfo,
  ProductType
} from '../../../../swagger/api/index';
import ProductStore from '../../../../stores/ProductStore';
import { ReactComponent as BizzIcon } from '../../../../layout/icons/payment/bizz.svg';
import { ReactComponent as LicensePlateIcon } from '../../../../layout/icons/payment/license-plate.svg';
import {
  ContentSmallCamptonBook,
  SmallGrey12,
  GreenLinkText
} from '../../../../layout/globalFonts';
import ContentStore from '../../../../stores/ContentStore';
import ManageUserAccessStore from '../../../../stores/ManageUserAccessStore';
import { overlayInnerStyle } from '../../../../common-components/TooltipStyling';
import useIsMobileView from '../../../../hooks/useIsMobileView';
import {
  isANPR,
  isOneTimeANPR,
  isTollingApp,
  isTollingBox
} from '../../../../utils/productTypeUtilities';

interface ProductCardHeaderProps {
  product: ProductInfo;
}

const Header = styled.div<{ ismobileview: boolean }>`
  position: relative;
  margin: 0;
  ${(props) => (props.ismobileview ? 'padding: 68px 20px 3.56vh;' : 'padding: 68px 10.65% 3.56vh;')}
  .ant-switch-checked {
    background: #00a050;
  }
`;

const ReferenceInput = styled(Input)`
  padding: 0;
  font-family: Campton Bold;
  font-size: 38px;
  color: #2d2d2d;
  letter-spacing: -0.3px;
  text-overflow: ellipsis;
  @media (max-width: 1000px) {
    font-size: 30px;
  }
`;

const StyledDivider = styled(Divider)`
  margin-top: 0px;
  background-color: #969696;
  border-radius: 5px;
  margin-bottom: 10px;
`;

const TitleRow = styled(Row)`
  margin-bottom: 10px;
`;

const StyledLicensePlateIcon = styled(LicensePlateIcon)`
  margin-top: 5px;
`;

const StyledBizzIcon = styled(BizzIcon)`
  margin-top: 2px;
`;

const getStatusBackgroundColor = (status?: IdentifierStatus, isEconomyBlocked?: boolean) => {
  if (isEconomyBlocked) {
    return '#FDD7DB'; // Splash Rose
  }

  switch (status) {
    case IdentifierStatus.Active:
      return '#CCF4E0'; // Splash Light Green
    case IdentifierStatus.Pending:
    case IdentifierStatus.BizzOnItsWay:
    case IdentifierStatus.CreditCardExpiresSoon:
      return '#FFF4CC'; // Yellow for pending
    case IdentifierStatus.Closed:
    case IdentifierStatus.Blocked:
    case IdentifierStatus.IssuesWithCreditCard:
    case IdentifierStatus.UnpaidInvoices:
      return '#FDD7DB'; // Splash Rose
    default:
      return '#CCF4E0';
  }
};

const PrivateStatusBadge = styled.text<{
  status?: IdentifierStatus;
  isEconomyBlocked?: boolean;
}>`
  padding: 8px 12px;
  border-radius: 4px;
  background: ${(props) => getStatusBackgroundColor(props.status, props.isEconomyBlocked)};
  cursor: ${(props) => (props.status === IdentifierStatus.Blocked ? 'pointer' : 'default')};
  font-family: Campton Book;
  -webkit-font-smoothing: subpixel-antialiased;
  -moz-osx-font-smoothing: auto;
  font-size: 18px;
  font-weight: 475;
  color: #2d2d2d;
  line-height: 24px;
`;

interface ProductCardHeaderProps {
  product: ProductInfo;
  contentStore: ContentStore;
  form?: FormInstance;
}

const ProductCardHeader: FC<ProductCardHeaderProps> = (props) => {
  const { product, contentStore, form } = props;
  const modalStore: ModalStore = useStore(ModalStore);
  const manageUserAccessStore: ManageUserAccessStore = useStore(ManageUserAccessStore);
  const productStore: ProductStore = useStore(ProductStore);
  const { productCardStore } = productStore;
  const isMobileView = useIsMobileView();
  const referenceInputRef = useRef<any>(null);

  let paymentIcon;
  switch (product.productType) {
    case ProductType.Bizz:
      paymentIcon = <StyledBizzIcon />;
      break;
    case ProductType.BizzCommercial:
      paymentIcon = <StyledBizzIcon />;
      break;
    case ProductType.LicensePlate:
      paymentIcon = <StyledLicensePlateIcon />;
      break;
    default:
  }

  let idNumberTitle = '';
  switch (product.productType) {
    case ProductType.Bizz:
      idNumberTitle = contentStore.cmsStore.getContent('BizzNumber')?.content ?? '';
      break;
    case ProductType.BizzCommercial:
      idNumberTitle = contentStore.cmsStore.getContent('BizzNumber')?.content ?? '';
      break;
    case ProductType.TollingBox:
      idNumberTitle = contentStore.cmsStore.getContent('Id&PanForTollBox')?.content ?? '';
      break;
    default:
      idNumberTitle = contentStore.cmsStore.getContent('VKnumber')?.content ?? '';
  }

  const productTypeText = contentStore.cmsStore.getContent(product.productType!)?.content;

  const referenceTitle = contentStore.cmsStore
    .getContent('NameOfProduct')
    ?.content.replace(
      '{productType}',
      contentStore.cmsStore.getContent(product.productType!)!.content
    );

  const idNumberTooltip = isTollingBox(product.productType)
    ? contentStore.cmsStore.getContent('Id&PanForTollBox')?.tooltip
    : !isANPR(product.productType) && !isTollingApp(product.productType)
    ? contentStore.cmsStore.getContent('Id&Pan')?.tooltip
    : '';

  const showStatusModal = () => {
    if (product.identifierStatus === IdentifierStatus.Blocked) {
      modalStore.openModal(
        <ProductIsBlockedModal contentStore={contentStore} productType={product.productType!} />
      );
    }
  };

  const updatePrivateProductReference = () => {
    const values: IdentifierInfo = JSON.parse(JSON.stringify(product));
    const changedValues = form!.getFieldsValue();
    values.reference = changedValues.reference;
    productCardStore.saveProductcardChanges(product.identifierId!, values);
  };

  const renderIdAndPanWithDropdown = (
    <Tooltip
      placement="bottom"
      title={idNumberTooltip}
      color="#2d2d2d"
      overlayInnerStyle={overlayInnerStyle}
    >
      <Col>
        <TitleRow>
          <SmallGrey12>{idNumberTitle}</SmallGrey12>
        </TitleRow>
        <Row>
          <PanDropdown product={product} />
        </Row>
      </Col>
    </Tooltip>
  );

  const remainingTrips = (
    <Col>
      <TitleRow>
        <SmallGrey12>{contentStore.cmsStore.getContent('RemainingTrips')?.content}</SmallGrey12>
      </TitleRow>
      <Row>
        <Col>
          <ContentSmallCamptonBook>{product.remainingTrips}</ContentSmallCamptonBook>
        </Col>
      </Row>
    </Col>
  );

  const isEconomyBlocked = manageUserAccessStore.isEconomyBlocked();

  const getPrivateStatusText = () => {
    if (isEconomyBlocked) {
      return 'EconomyBlockedIdentifier';
    }
    switch (product.identifierStatus) {
      case IdentifierStatus.Active:
        return 'ActiveProduct';
      case IdentifierStatus.BizzOnItsWay:
        return 'BizzOnItsWay';
      case IdentifierStatus.Pending:
        return 'PrivateBizzPending';
      case IdentifierStatus.CreditCardExpiresSoon:
        return 'PaymentCardExpiresSoon';
      case IdentifierStatus.Blocked:
        return `${product.productType}IsBlocked`;
      case IdentifierStatus.UnpaidInvoices:
        return 'MissingPaymentOnIdentifier';
      case IdentifierStatus.IssuesWithCreditCard:
        return 'IssusesWithCreditCard';
      default:
        return '';
    }
  };

  const renderPrivateProductStatusBadge = (
    <PrivateStatusBadge
      status={product.identifierStatus}
      isEconomyBlocked={isEconomyBlocked}
      onClick={showStatusModal}
    >
      {contentStore.cmsStore.getContent(getPrivateStatusText())?.content}
    </PrivateStatusBadge>
  );

  return (
    <Header ismobileview={isMobileView}>
      <Row align="bottom" justify="space-between" gutter={16} style={{ flexWrap: 'nowrap' }}>
        <Col>
          <SmallGrey12>{referenceTitle}</SmallGrey12>
        </Col>
        <Col>{renderPrivateProductStatusBadge}</Col>
      </Row>
      <Row align="bottom" justify="space-between" gutter={16} style={{ flexWrap: 'nowrap' }}>
        <Col flex={1}>
          <Row>
            <Form.Item name="reference" noStyle>
              <ReferenceInput
                ref={referenceInputRef}
                maxLength={30}
                bordered={false}
                onBlur={() => {
                  if (form?.getFieldValue('reference') !== product.reference) {
                    updatePrivateProductReference();
                  }
                }}
              />
            </Form.Item>
          </Row>
        </Col>
      </Row>
      <StyledDivider />
      <Row style={{ marginBottom: '20px' }}>
        <GreenLinkText
          style={{ cursor: 'pointer' }}
          onClick={() => {
            if (referenceInputRef.current) {
              referenceInputRef.current.focus();
            }
          }}
        >
          {contentStore.cmsStore.getContent('editNickname')?.content}
        </GreenLinkText>
      </Row>
      <Row gutter={[48, 14]}>
        <Col>
          <TitleRow>
            <SmallGrey12>{contentStore.cmsStore.getContent('productHeader')?.content}</SmallGrey12>
          </TitleRow>
          <Row gutter={8} wrap={false}>
            <Col>{paymentIcon}</Col>
            <Col style={{ whiteSpace: 'nowrap' }}>
              <ContentSmallCamptonBook>{productTypeText}</ContentSmallCamptonBook>
            </Col>
          </Row>
        </Col>
        {isOneTimeANPR(product.productType)
          ? remainingTrips
          : !isMobileView && renderIdAndPanWithDropdown}
      </Row>
      {isMobileView && <Row style={{ paddingTop: '10px' }}>{renderIdAndPanWithDropdown}</Row>}
    </Header>
  );
};

export default observer(ProductCardHeader);

ProductCardHeader.defaultProps = {
  form: undefined
};
